import { z } from 'zod';
import { MerchantIdSchema } from './MerchantIdSchema';
import { AddressSchema } from './AddressSchema';

const MerchantContactSchema = z
  .object({
    contactEmail: z.string().optional(),
    contactFirstName: z.string().optional(),
    contactLastName: z.string().optional(),
    contactPhone: z.string().optional()
  })
  .optional();

const MerchantSchema = z
  .object({
    id: MerchantIdSchema,
    merchantEncodedKey: z.string({
      required_error: 'merchantEncodedKey is required',
      invalid_type_error: 'merchantEncodedKey must be a string'
    }),
    accountName: z.string({
      required_error: 'accountName is required',
      invalid_type_error: 'accountName must be a string'
    }),
    tradingName: z.string({
      required_error: 'tradingName is required',
      invalid_type_error: 'tradingName must be a string'
    }),
    accountOwner: z
      .string({
        required_error: 'accountOwner is required',
        invalid_type_error: 'accountOwner must be a string'
      })
      .optional(),
    billingAddress: AddressSchema.optional(),
    shippingAddress: AddressSchema.optional(),

    type: z
      .string({
        required_error: 'type is required',
        invalid_type_error: 'type must be a string'
      })
      .optional(),
    website: z
      .string({
        required_error: 'website is required',
        invalid_type_error: 'website must be a string'
      })
      .optional(),
    abn: z
      .string({
        required_error: 'abn is required',
        invalid_type_error: 'abn must be a string'
      })
      .optional(),
    accountStatus: z
      .string({
        required_error: 'accountStatus is required',
        invalid_type_error: 'accountStatus must be a string'
      })
      .optional(),

    accreditationDate: z
      .string()
      .datetime({
        message: 'accreditationDate must be a Date'
      })
      .optional(),
    accreditationReviewDate: z
      .string()
      .datetime({
        message: 'accreditationReviewDate must be a Date'
      })
      .optional(),
    merchantContact: MerchantContactSchema,
    canManuallyRequestFunds: z
      .boolean({
        required_error: 'canManuallyRequestFunds is required',
        invalid_type_error: 'canManuallyRequestFunds must be a boolean'
      })
      .optional(),
    needsManualReviewBeforeDowngrade: z
      .boolean({
        required_error: 'needsManualReviewBeforeDowngrade is required',
        invalid_type_error: 'needsManualReviewBeforeDowngrade must be a boolean'
      })
      .optional()
      .default(false),
    rtoNumber: z
      .string({
        required_error: 'rtoNumber is required',
        invalid_type_error: 'rtoNumber must be a string'
      })
      .optional(),
    zeptoContactId: z
      .string({
        required_error: 'zeptoContactId is required',
        invalid_type_error: 'zeptoContactId must be a string'
      })
      .optional(),

    availableProducts: z.array(z.string().uuid('Not a valid uuid')),
    purposeOfLoanLabel: z
      .string({
        required_error: 'purposeOfLoanLabel is required',
        invalid_type_error: 'purposeOfLoanLabel must be a string'
      })
      .optional(),

    merchantDeleted: z
      .boolean({
        required_error: 'merchantDeleted is required',
        invalid_type_error: 'merchantDeleted must be a boolean'
      })
      .optional(),

    sameTermsFlag: z
      .boolean({
        required_error: 'sameTermsFlag is required',
        invalid_type_error: 'sameTermsFlag must be a boolean'
      })
      .optional()
      .default(false),

    merchantOnPackage: z
      .boolean({
        required_error: 'merchantOnPackage is required',
        invalid_type_error: 'merchantOnPackage must be a boolean'
      })
      .optional()
      .default(false),

    // REQURED IS INTENTIONAL, not a typo!
    studentNumberRequred: z
      .boolean({
        required_error: 'studentNumberRequred is required',
        invalid_type_error: 'studentNumberRequred must be a boolean'
      })
      .optional(),

    merchantMinScoreRequired: z
      .boolean({
        required_error: 'merchantMinScoreRequired is required',
        invalid_type_error: 'merchantMinScoreRequired must be a boolean'
      })
      .optional()
      .default(false),

    approvePendingIdFlag: z
      .boolean({
        required_error: 'approvePendingIdFlag is required',
        invalid_type_error: 'approvePendingIdFlag must be a boolean'
      })
      .optional()
      .default(false),

    completesPASApplication: z
      .boolean({
        required_error: 'completesPASApplication is required',
        invalid_type_error: 'completesPASApplication must be a boolean'
      })
      .optional()
      .default(false)
  })
  .strict();

type TMerchantSchema = ReturnType<typeof MerchantSchema.parse>;

export { type TMerchantSchema, MerchantSchema };
